/**
 * deliverables-route.js — client-saas /deliverables (reseller persona)
 *
 * Every client deliverable (Advertising Plan first) at a glance — who is still
 * authoring, who owes a review, what came back — plus the AUTHORING drawer,
 * which is the delta from the questionnaires page: a questionnaire is sent as
 * a fixed form, a deliverable is WRITTEN here first (the 'am' fields), then
 * sent for the client's review.
 *
 * ONE ROW PER DELIVERABLE, not per client — deliberately different from
 * /questionnaires. A kickoff questionnaire happens once per client, so "status
 * per client" is the question there. Deliverables recur (next month's plan is
 * a NEW deliverable; a requested-changes round is NOT — it revises this one
 * in place via POST /:guid/revise, which archives the returned review), so
 * collapsing to one row per client would hide exactly the history this page
 * exists to show.
 *
 * Lifecycle surfaced here: New deliverable (pick client + kind, kinds come
 * from GET /templates — never hardcoded) -> author in the drawer -> Send
 * (email + link) -> Copy link while awaiting -> Read review once returned ->
 * Revise (archives the round, reopens authoring, prior feedback pinned above
 * the form) -> re-send. Status vocabulary is the DB's own
 * (DRAFT/SENT/SUBMITTED/CANCELLED); the labels translate for humans, one map,
 * so pills and badges cannot disagree.
 *
 * The authoring form and the fill page are two halves of one contract:
 * fields render with data-question-id/data-question-type and are read back by
 * the SHARED collectAnswers (shared/questionnaireAnswers.js) — the same
 * renderer/collector split every questionnaire surface uses.
 */

import AuthController from '../services/AuthController.js';
import PubSub from 'pubsub-js';
import AppBroadcast from '../../../../shared/appBroadcast.js';
import {
  answerDisplay, editFieldHtml, collectAnswers, answersMetaHtml, fmtDateTime, ensureQuestionnaireAnswerStyles,
} from '../../../../shared/questionnaireAnswers.js';
// Repeat-group contract (uncapped campaigns): the same module the API's
// audience write-filter and the public fill page use.
import {
  repeatFamily, prototypeQuestions, instanceQuestions, maxInstance, expandSections,
} from '../../../../shared/deliverableRepeats.js';
import { capList } from '../core/listExpand.js';
import { guardedFetch } from '../core/navGeneration.js';
import { isDenial, denialState, failIfNotOk } from '../core/denialSurface.js';

const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ESC[c]);
const apiBase = () => AuthController.getConfig?.().BASE_URL || '';
const toast = (message, type = 'info') => { try { PubSub.publish(AppBroadcast.UI_TOAST, { message, type }); } catch { /* no-op */ } };

const MANAGED_CHANNEL = 'managed_client';

const STATUSES = [
  ['ALL', 'All'],
  ['DRAFT', 'In progress'],
  ['SENT', 'Awaiting approval'],
  ['SUBMITTED', 'Returned'],
  ['CANCELLED', 'Cancelled'],
];
const STATUS_LABEL = Object.fromEntries(STATUSES);

// clients/templates are TRI-STATE and the distinction is load-bearing:
//   null  — unknown, we could not read them (denied, or broken)
//   []    — known to be empty
//   [...] — known
// Collapsing those two into an empty array is the bug this page had: a reseller with a
// full book, denied acctGroup, would be told "No clients yet. Add a client first."
// Confidently wrong is worse than obviously broken. clientsErr/templatesErr carry WHY,
// because "you are not allowed to see this" and "this did not load" need different words.
const state = {
  rows: [], clients: null, templates: null,
  clientsErr: null, templatesErr: null,      // null | 'denied' | 'broken'
  filter: 'ALL', search: '',
};

let stylesInjected = false;
function ensureStyles() {
  ensureQuestionnaireAnswerStyles();
  if (stylesInjected) return;
  const style = document.createElement('style');
  style.id = 'm5t-deliverables-styles';
  style.textContent = `
    .page-shell .dz-bar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 14px; }
    .page-shell .dz-filter { display: inline-flex; gap: 4px; background: var(--color-surface, #F4F7FB); border-radius: 999px; padding: 3px; flex-wrap: wrap; }
    .page-shell .dz-filter button { appearance: none; border: 0; background: transparent; cursor: pointer; font: inherit; font-size: 0.78rem; font-weight: 600; padding: 6px 13px; border-radius: 999px; color: var(--color-text-muted, #4A5A78); }
    .page-shell .dz-filter button.active { background: var(--color-bg, #fff); color: var(--color-accent-hover, #00558D); box-shadow: 0 1px 2px rgba(11,18,32,0.12); }
    .page-shell .dz-pillnum { font-weight: 500; opacity: 0.65; margin-left: 3px; }
    .page-shell .dz-searchwrap { position: relative; margin-left: auto; }
    .page-shell .dz-searchwrap .material-symbols-outlined { position: absolute; left: 9px; top: 50%; transform: translateY(-50%); font-size: 17px; color: var(--color-text-subtle, #6C7C9A); pointer-events: none; }
    .page-shell .dz-search { font: inherit; font-size: 0.84rem; padding: 7px 12px 7px 32px; border-radius: 10px; border: 1px solid var(--color-border, #D4DCE8); background: var(--color-bg, #fff); color: var(--color-text, #0B1220); width: 220px; }
    .page-shell .dz-search:focus { outline: none; border-color: var(--color-primary-600, #0072BA); box-shadow: 0 0 0 3px var(--color-accent-soft, rgba(0,114,186,0.12)); }
    .page-shell .dz-count { font-size: 0.82rem; color: var(--color-text-subtle, #6C7C9A); white-space: nowrap; }
    .page-shell .dz-new { appearance: none; border: 0; cursor: pointer; font: inherit; font-size: 0.82rem; font-weight: 600; color: #fff; background: var(--color-primary-600, #0072BA); padding: 8px 14px; border-radius: 10px; display: inline-flex; align-items: center; gap: 5px; }
    .page-shell .dz-new:hover { background: var(--color-primary-700, #00558D); }
    .page-shell .dz-new .material-symbols-outlined { font-size: 1.05rem; }

    .page-shell .dz-tablewrap { overflow-x: auto; }
    .page-shell .dz-table { width: 100%; border-collapse: collapse; font-size: 0.88rem; min-width: 760px; }
    .page-shell .dz-table th { text-align: left; font-size: 0.68rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--color-text-subtle, #6C7C9A); padding: 8px 12px; border-bottom: 1px solid var(--color-border, #D4DCE8); white-space: nowrap; }
    .page-shell .dz-table td { padding: 11px 12px; border-bottom: 1px solid var(--color-surface-2, #EAF0F7); vertical-align: middle; }
    .page-shell .dz-table tr:last-child td { border-bottom: 0; }
    .page-shell .dz-row--open { cursor: pointer; }
    .page-shell .dz-row--open:hover td { background: var(--color-surface, #F7FAFD); }
    .page-shell .dz-client { font-weight: 600; }
    .page-shell .dz-kind { color: var(--color-text-muted, #4A5A78); }
    .page-shell .dz-rev { font-size: 0.68rem; font-weight: 700; color: var(--color-text-subtle, #6C7C9A); margin-left: 6px; white-space: nowrap; }
    .page-shell .dz-when { color: var(--color-text-subtle, #6C7C9A); white-space: nowrap; }
    .page-shell .dz-badge { display: inline-block; white-space: nowrap; font-size: 0.66rem; font-weight: 700; letter-spacing: 0.03em; padding: 3px 9px; border-radius: 999px; background: var(--color-surface-2, #EAF0F7); color: var(--color-text-muted, #4A5A78); }
    .page-shell .dz-badge[data-s="SUBMITTED"] { background: #DCFCE7; color: #047857; }
    .page-shell .dz-badge[data-s="SENT"] { background: #FEF3C7; color: #92400E; }
    .page-shell .dz-badge[data-s="DRAFT"] { background: var(--color-accent-soft, #EEF4FF); color: var(--color-accent-hover, #00558D); }
    .page-shell .dz-badge[data-s="CANCELLED"] { background: var(--color-danger-soft, #FBE3DE); color: var(--color-danger, #DD4124); }
    .page-shell .dz-act { appearance: none; border: 1px solid var(--color-border, #D4DCE8); background: transparent; cursor: pointer; font: inherit; font-size: 0.78rem; font-weight: 600; color: var(--color-accent, #0072BA); padding: 5px 11px; border-radius: 8px; white-space: nowrap; }
    .page-shell .dz-act:hover { background: var(--color-accent-soft, #EEF4FF); }
    .page-shell td.dz-actcell { text-align: right; }

    /* Drawer — same shape as the Questionnaires/Conversations drawers, wider:
       the authoring form is a real document. */
    .dz-drawer-scrim { position: fixed; inset: 0; background: rgba(11,18,32,0.4); z-index: 2000; opacity: 0; transition: opacity 0.18s ease; }
    .dz-drawer-scrim.open { opacity: 1; }
    .dz-drawer { position: fixed; top: 0; right: 0; height: 100%; width: min(880px, 96vw); background: var(--color-bg, #fff); color: var(--color-text, #0B1220); box-shadow: -12px 0 40px rgba(11,18,32,0.25); z-index: 2001; transform: translateX(100%); transition: transform 0.22s ease; display: flex; flex-direction: column; }
    .dz-drawer.open { transform: translateX(0); }
    .dz-drawer__head { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 18px 20px; border-bottom: 1px solid var(--color-border, #D4DCE8); }
    .dz-drawer__title { font-size: 1.02rem; font-weight: 600; }
    .dz-drawer__sub { font-size: 0.76rem; color: var(--color-text-subtle, #6C7C9A); margin-top: 2px; }
    .dz-drawer__close { appearance: none; border: 0; background: transparent; cursor: pointer; font-size: 1.5rem; line-height: 1; color: var(--color-text-muted, #4A5A78); width: 32px; height: 32px; border-radius: 8px; flex: 0 0 auto; }
    .dz-drawer__close:hover { background: var(--color-surface, #F4F7FB); }
    .dz-drawer__body { flex: 1 1 auto; overflow-y: auto; padding: 20px; }
    .dz-drawer__foot { flex: 0 0 auto; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; padding: 14px 20px; border-top: 1px solid var(--color-border, #D4DCE8); background: var(--color-surface, #F7FAFD); }
    html.ff-no-animations .dz-drawer, html.ff-no-animations .dz-drawer-scrim { transition: none; }
    @media (prefers-reduced-motion: reduce) { .dz-drawer, .dz-drawer-scrim { transition: none; } }

    .dz-drawer .dz-intro { font-size: 0.82rem; color: var(--color-text-muted, #4A5A78); margin: -4px 0 12px; }
    .dz-drawer .dz-note { font-size: 0.78rem; color: var(--color-text-subtle, #6C7C9A); margin: 0 0 14px; }
    .dz-drawer .dz-field { margin-bottom: 14px; }
    .dz-drawer .dz-field > label { display: block; font-size: 0.8rem; font-weight: 600; margin-bottom: 5px; }
    .dz-drawer .dz-help { font-size: 0.74rem; color: var(--color-text-subtle, #6C7C9A); margin: -2px 0 5px; }
    .dz-drawer select, .dz-drawer input[type="email"] {
      width: 100%; box-sizing: border-box; font: inherit; font-size: 0.88rem; padding: 8px 10px;
      border: 1px solid var(--color-border, #D4DCE8); border-radius: var(--radius-md, 8px); background: var(--color-bg, #fff); color: var(--color-text, #0B1220);
    }
    .dz-drawer .dz-btn { appearance: none; border: 0; cursor: pointer; font: inherit; font-size: 0.84rem; font-weight: 600; color: #fff; background: var(--color-primary-600, #0072BA); padding: 9px 16px; border-radius: 9px; white-space: nowrap; display: inline-flex; align-items: center; gap: 6px; }
    .dz-drawer .dz-btn .material-symbols-outlined { font-size: 1.05rem; }
    .dz-drawer .dz-btn:hover { background: var(--color-primary-700, #00558D); }
    .dz-drawer .dz-btn[disabled] { opacity: 0.6; cursor: default; }
    .dz-drawer .dz-btn--ghost { appearance: none; cursor: pointer; font: inherit; font-size: 0.84rem; font-weight: 600; padding: 9px 14px; border-radius: 9px; border: 1px solid var(--color-border, #D4DCE8); background: var(--color-bg, #fff); color: var(--color-text, #0B1220); white-space: nowrap; display: inline-flex; align-items: center; gap: 6px; }
    .dz-drawer .dz-btn--ghost .material-symbols-outlined { font-size: 1.05rem; }
    .dz-drawer .dz-btn--ghost:hover { background: var(--color-surface, #F4F7FB); }
    .dz-drawer .dz-btn--danger { color: var(--color-danger, #DD4124); border-color: var(--color-danger-soft, #FBE3DE); }
    /* authoring roller chrome — sticky, so section navigation is never below
       the fold of a long step. */
    .dz-drawer .dz-steps-head { position: sticky; top: -20px; z-index: 5; background: var(--color-bg, #fff); display: flex; align-items: center; justify-content: space-between; gap: 12px; margin: -8px 0 14px; padding: 12px 0 10px; border-bottom: 1px solid var(--color-surface-2, #EAF0F7); }
    .dz-drawer .dz-step-label { font-size: 0.86rem; font-weight: 700; color: var(--color-primary-700, #00558D); }
    .dz-drawer .dz-steps-ctl { display: flex; align-items: center; gap: 10px; }
    .dz-drawer .dz-arrow { appearance: none; border: 1px solid var(--color-border, #D4DCE8); background: var(--color-bg, #fff); cursor: pointer; width: 28px; height: 28px; border-radius: 8px; display: inline-flex; align-items: center; justify-content: center; color: var(--color-primary-600, #0072BA); padding: 0; }
    .dz-drawer .dz-arrow .material-symbols-outlined { font-size: 1.15rem; }
    .dz-drawer .dz-arrow:hover { background: var(--color-surface, #F4F7FB); }
    .dz-drawer .dz-arrow[disabled] { opacity: 0.35; cursor: default; }
    /* Section headings AND field labels in drawer context: full ink, never
       faint gray (readability ruling, twice). */
    .dz-drawer .qm-section h3 { color: var(--color-primary-800, #003961); font-size: 1rem; }
    .dz-drawer .qm-qa__q { color: var(--color-text, #0B1220); }
    .dz-drawer .dz-dots { display: flex; gap: 8px; }
    .dz-drawer .dz-dot { appearance: none; border: 0; cursor: pointer; padding: 0; width: 10px; height: 10px; border-radius: 50%; background: var(--color-border, #D4DCE8); transition: background .15s ease, transform .15s ease; }
    .dz-drawer .dz-dot[aria-current="step"] { background: var(--color-primary-600, #0072BA); transform: scale(1.25); }
    .dz-drawer .dz-step[hidden] { display: none; }
    .dz-drawer .dz-step-nav { display: flex; justify-content: space-between; gap: 10px; margin-top: 16px; }
    .dz-drawer .dz-cblock { border: 1px solid var(--color-border, #D4DCE8); border-radius: var(--radius-md, 10px); padding: 12px 14px; margin: 0 0 12px; }
    .dz-drawer .dz-cblock[hidden] { display: none; }
    .dz-drawer .dz-cblock__title { font-size: 0.82rem; font-weight: 700; margin: 0 0 10px; color: var(--color-primary-600, #0072BA); }
    .dz-drawer .dz-addblock { display: block; width: 100%; margin: 0 0 12px; }
    .dz-drawer .dz-addblock[hidden] { display: none; }
    .dz-drawer .dz-feedback { background: var(--color-warning-soft, #FDF6E3); border: 1px solid var(--color-border, #D4DCE8); border-radius: var(--radius-md, 10px); padding: 12px 14px; margin: 0 0 16px; }
    .dz-drawer .dz-feedback__title { font-size: 0.72rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--color-warning-text, #92400E); margin: 0 0 8px; }
    .dz-drawer .dz-feedback .qm-qa { margin-bottom: 8px; }
    .dz-drawer .dz-linkrow { display: flex; gap: 8px; align-items: center; margin: 0 0 14px; }
    .dz-drawer .dz-linkrow input { flex: 1 1 auto; font: inherit; font-size: 0.78rem; padding: 8px 10px; border: 1px solid var(--color-border, #D4DCE8); border-radius: 8px; background: var(--color-surface, #F4F7FB); color: var(--color-text-muted, #4A5A78); }
    .dz-drawer .dz-sendrow { display: flex; gap: 8px; align-items: center; flex: 1 1 auto; }
    .dz-drawer .dz-sendrow input { flex: 1 1 auto; }
  `;
  document.head.appendChild(style);
  stylesInjected = true;
}

const emptyState = (icon, msg) =>
  `<div class="m5t-empty"><span class="material-symbols-outlined" aria-hidden="true">${icon}</span><p>${msg}</p></div>`;

function visibleRows() {
  const q = state.search.trim().toLowerCase();
  return state.rows.filter((r) => (state.filter === 'ALL' || r.status === state.filter)
    && (!q || String(r.owner_name || '').toLowerCase().includes(q)));
}

// Per-status primary action. Rows also open the drawer on click (except CANCELLED,
// which has nothing left to act on — its history still shows in the table).
function actionHtml(r) {
  if (r.status === 'DRAFT') return `<button class="dz-act" type="button" data-act="open" data-guid="${esc(r.guid)}">Edit</button>`;
  if (r.status === 'SENT') return `<button class="dz-act" type="button" data-act="copy" data-url="${esc(r.url || '')}">Copy link</button>`;
  if (r.status === 'SUBMITTED') return `<button class="dz-act" type="button" data-act="open" data-guid="${esc(r.guid)}">Read review</button>`;
  return '<span class="dz-when">—</span>';
}

function rowHTML(r) {
  const openable = r.status !== 'CANCELLED';
  return `<tr class="dz-row${openable ? ' dz-row--open' : ''}"${openable ? ` data-open-guid="${esc(r.guid)}"` : ''}>
    <td><span class="dz-client">${esc(r.owner_name || r.owner_guid)}</span></td>
    <td><span class="dz-kind">${esc(r.template_name || r.kind || '')}</span>${Number(r.revision) > 1 ? `<span class="dz-rev">Rev ${esc(r.revision)}</span>` : ''}</td>
    <td><span class="dz-badge" data-s="${esc(r.status)}">${esc(STATUS_LABEL[r.status] || r.status)}</span></td>
    <td class="dz-when">${r.sent_tz ? esc(fmtDateTime(r.sent_tz)) : '—'}</td>
    <td class="dz-when">${r.submitted_tz ? esc(fmtDateTime(r.submitted_tz)) : '—'}</td>
    <td class="dz-actcell">${actionHtml(r)}</td>
  </tr>`;
}

export function render() {
  ensureStyles();
  return `
    <div class="page-shell" data-section="deliverables">
      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">Deliverables</h1>
          <p class="page-description">Plans and sign-off status for every client.</p>
        </div>
      </div>

      <div class="dashboard-card">
        <div class="card-header">
          <h2 class="card-title"><span class="material-symbols-outlined" aria-hidden="true">approval</span> All deliverables</h2>
          <button class="dz-new" type="button" id="dz-new"><span class="material-symbols-outlined" aria-hidden="true">add</span>New deliverable</button>
        </div>
        <div class="card-body" id="dz-body">${emptyState('hourglass_empty', 'Loading…')}</div>
      </div>
    </div>`;
}

export async function init() {
  document.getElementById('dz-new')?.addEventListener('click', openNewDrawer);
  await load();
}

async function load() {
  const body = document.getElementById('dz-body');
  if (!body) return;
  try {
    // owner_guid deliberately omitted: the endpoint falls back to the caller's
    // whole owner scope — the cross-client list this page wants. Clients and
    // templates feed the "new deliverable" picker.
    const [dres, cres, tres] = await Promise.all([
      guardedFetch(`${apiBase()}/m5t/v5/m5mDeliverable`),
      guardedFetch(`${apiBase()}/m5t/v5/acctGroup`),
      guardedFetch(`${apiBase()}/m5t/v5/m5mDeliverable/templates`),
    ]);
    const ddata = await dres.json().catch(() => ({}));
    const cdata = await cres.json().catch(() => ({}));
    const tdata = await tres.json().catch(() => ({}));
    // REQUIRED-NESS FOLLOWS WHAT A RESPONSE IS FOR, not what the page happens to fetch
    // together. dres is the content the user came for and is entitled to. cres and tres
    // populate one button's drawer — cres the client select (:395) and tres the kind
    // select (:399). Letting a picker input take the page down inverts that: the user
    // loses what they can have to protect something they cannot use anyway.
    failIfNotOk(dres, ddata);
    state.rows = ddata.data || [];

    // Optional, and the failure reason is kept rather than flattened.
    state.clientsErr = cres.ok ? null : (cres.status === 403 ? 'denied' : 'broken');
    state.clients = cres.ok
      ? (cdata.data || [])
        .filter((g) => g.class === MANAGED_CHANNEL && !['DELETED', 'ARCHIVED'].includes(g.status))
        .sort((a, b) => String(a.caption || '').localeCompare(String(b.caption || '')))
      : null;   // null, NOT [] — see the state declaration
    state.templatesErr = tres.ok ? null : (tres.status === 403 ? 'denied' : 'broken');
    state.templates = tres.ok ? (tdata.data || []) : null;
  } catch (err) {
    body.innerHTML = isDenial(err) ? denialState() : emptyState('error', 'Could not load deliverables. Please try again.');
    return;
  }
  renderBody();
}

/** Cap the table. Called from BOTH render paths so search cannot bypass it. */
function capDzRows() {
  const t = document.getElementById('dz-tbody');
  if (t) capList(t, { rowSelector: 'tr.dz-row', noun: 'deliverables', controlHost: t.closest('.dz-tablewrap') || t.parentElement });
}

function renderBody() {
  const body = document.getElementById('dz-body');
  if (!body) return;

  // KNOWN-empty only. With state.clients null we do not know whether there are clients,
  // so this copy would be a claim we cannot make — and the wrong one for the case that
  // matters, a reseller denied acctGroup. Falls through to the deliverables empty state,
  // which is true either way.
  if (state.clients?.length === 0 && !state.rows.length) {
    body.innerHTML = emptyState('groups', 'No clients yet. Add a client first, then author their first deliverable.');
    return;
  }
  if (!state.rows.length) {
    body.innerHTML = emptyState('approval', 'No deliverables yet. Author the first one with New deliverable.');
    return;
  }

  const counts = { ALL: state.rows.length };
  STATUSES.slice(1).forEach(([k]) => { counts[k] = state.rows.filter((r) => r.status === k).length; });
  const visible = visibleRows();

  body.innerHTML = `
    <div class="dz-bar">
      <div class="dz-filter">
        ${STATUSES.map(([k, label]) => `<button data-filter="${k}" class="${state.filter === k ? 'active' : ''}">${esc(label)}${counts[k] ? `<span class="dz-pillnum">${counts[k]}</span>` : ''}</button>`).join('')}
      </div>
      <span class="dz-searchwrap">
        <span class="material-symbols-outlined" aria-hidden="true">search</span>
        <input class="dz-search" id="dz-search" type="search" placeholder="Search clients" value="${esc(state.search)}" aria-label="Search clients" />
      </span>
      <span class="dz-count">${visible.length} of ${state.rows.length}</span>
    </div>
    <div class="dz-tablewrap">
      <table class="dz-table">
        <thead><tr><th>Client</th><th>Deliverable</th><th>Status</th><th>Sent</th><th>Returned</th><th></th></tr></thead>
        <tbody id="dz-tbody">${visible.length
          ? visible.map(rowHTML).join('')
          : `<tr><td colspan="6"><div class="dz-when" style="padding:14px 4px">${state.search.trim() ? 'No deliverables match your search.' : `No deliverables with status “${esc(STATUS_LABEL[state.filter] || state.filter)}”.`}</div></td></tr>`}</tbody>
      </table>
    </div>`;

  capDzRows();

  body.querySelector('.dz-filter')?.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-filter]');
    if (!btn) return;
    state.filter = btn.dataset.filter;
    renderBody();
  });
  body.querySelector('#dz-search')?.addEventListener('input', (e) => {
    state.search = e.target.value;
    renderTableOnly();
  });
  body.querySelector('#dz-tbody')?.addEventListener('click', onRowClick);
}

// Table-only re-render so typing does not steal focus from the search box.
function renderTableOnly() {
  const tbody = document.getElementById('dz-tbody');
  if (!tbody) return;
  const visible = visibleRows();
  tbody.innerHTML = visible.length
    ? visible.map(rowHTML).join('')
    : `<tr><td colspan="6"><div class="dz-when" style="padding:14px 4px">No deliverables match your search.</div></td></tr>`;
  capDzRows();
  const count = document.querySelector('.dz-count');
  if (count) count.textContent = `${visible.length} of ${state.rows.length}`;
}

function onRowClick(e) {
  const copyBtn = e.target.closest('[data-act="copy"]');
  if (copyBtn) {
    e.stopPropagation();
    const url = copyBtn.dataset.url;
    if (!url) { toast('No link on this deliverable yet.', 'warning'); return; }
    navigator.clipboard?.writeText(url).then(
      () => { copyBtn.textContent = 'Copied!'; setTimeout(() => { copyBtn.textContent = 'Copy link'; }, 1500); },
      () => toast('Could not copy the link.', 'error'),
    );
    return;
  }
  const openBtn = e.target.closest('[data-act="open"]');
  const row = e.target.closest('[data-open-guid]');
  const guid = openBtn?.dataset.guid || row?.dataset.openGuid;
  if (guid) openDrawer(guid);
}

// ── drawer chrome (shared by "new", authoring and review modes) ──────────────
function makeDrawer(label) {
  const scrim = document.createElement('div');
  scrim.className = 'dz-drawer-scrim';
  const drawer = document.createElement('aside');
  drawer.className = 'dz-drawer';
  drawer.setAttribute('role', 'dialog');
  drawer.setAttribute('aria-label', label);
  drawer.innerHTML = `
    <div class="dz-drawer__head">
      <div><div class="dz-drawer__title" id="dz-dtitle">${esc(label)}</div><div class="dz-drawer__sub" id="dz-dsub"></div></div>
      <button class="dz-drawer__close" type="button" aria-label="Close">&times;</button>
    </div>
    <div class="dz-drawer__body" id="dz-dbody">${emptyState('hourglass_empty', 'Loading…')}</div>
    <div class="dz-drawer__foot" id="dz-dfoot"></div>`;
  document.body.append(scrim, drawer);
  requestAnimationFrame(() => { scrim.classList.add('open'); drawer.classList.add('open'); });

  const close = () => {
    scrim.classList.remove('open');
    drawer.classList.remove('open');
    setTimeout(() => { scrim.remove(); drawer.remove(); }, 220);
    document.removeEventListener('keydown', onKey);
  };
  function onKey(ev) { if (ev.key === 'Escape') { ev.preventDefault(); close(); } }
  document.addEventListener('keydown', onKey);
  scrim.addEventListener('click', close);
  drawer.querySelector('.dz-drawer__close').addEventListener('click', close);
  return { drawer, close };
}

// ── "new deliverable": pick a client and a kind, create the DRAFT ────────────
function openNewDrawer() {
  // Three different facts, three different messages. A single "unavailable" would
  // collapse denied and broken back together, which is what this change exists to undo.
  if (state.clients === null) {
    toast(state.clientsErr === 'denied'
      ? 'You do not have access to the client list, so a deliverable cannot be started here.'
      : 'The client list could not be loaded. Refresh and try again.',
    state.clientsErr === 'denied' ? 'warning' : 'error');
    return;
  }
  if (!state.clients.length) { toast('Add a client first.', 'warning'); return; }
  if (state.templates === null) {
    toast(state.templatesErr === 'denied'
      ? 'You do not have access to deliverable templates.'
      : 'Deliverable templates could not be loaded. Refresh and try again.',
    state.templatesErr === 'denied' ? 'warning' : 'error');
    return;
  }
  if (!state.templates.length) { toast('No deliverable templates are configured.', 'error'); return; }
  const { drawer, close } = makeDrawer('New deliverable');
  drawer.querySelector('#dz-dbody').innerHTML = `
    <div class="dz-field">
      <label for="dz-new-client">Client</label>
      <select id="dz-new-client">${state.clients.map((c) => `<option value="${esc(c.guid)}">${esc(c.caption || c.name || c.guid)}</option>`).join('')}</select>
    </div>
    <div class="dz-field">
      <label for="dz-new-kind">Deliverable</label>
      <select id="dz-new-kind">${state.templates.map((t) => `<option value="${esc(t.kind)}">${esc(t.name)}</option>`).join('')}</select>
    </div>
    <p class="dz-note">Creates a draft. Nothing is sent until you press Send to client.</p>`;
  const foot = drawer.querySelector('#dz-dfoot');
  foot.innerHTML = `<button class="dz-btn" type="button" id="dz-create">Start authoring</button>`;
  foot.querySelector('#dz-create').addEventListener('click', async () => {
    const ownerGuid = drawer.querySelector('#dz-new-client')?.value;
    const kind = drawer.querySelector('#dz-new-kind')?.value;
    const btn = foot.querySelector('#dz-create');
    btn.disabled = true;
    try {
      const res = await guardedFetch(`${apiBase()}/m5t/v5/m5mDeliverable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ owner_guid: ownerGuid, kind }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) throw new Error(json.error || 'create failed');
      close();
      await load();
      openDrawer(json.data.guid);
    } catch (err) {
      toast(err.message || 'Could not create the deliverable.', 'error');
      btn.disabled = false;
    }
  });
}

// ── authoring form: 'am' fields only, via the shared field/collector contract ─
// Rendered as a ROLLER (one section per step, every step stays mounted so
// collectAnswers reads them all) with campaign groups as PROGRESSIVE blocks:
// block 1 shows, "+ Add ..." reveals the next — a four-campaign wall of empty
// fields was too much on one screen (owner ruling 2026-08-19).
function authorFieldHtml(q, content, labelOverride) {
  return `
    <div class="qm-qa" data-question-id="${esc(q.id)}" data-question-type="${esc(q.type)}">
      <div class="qm-qa__q">${esc(labelOverride ?? q.label)}${q.help ? ` <span class="dz-help" style="display:inline;font-weight:400">(${esc(q.help)})</span>` : ''}</div>
      <div class="qm-qa__a">${editFieldHtml(q, content)}</div>
    </div>`;
}

/** Does any question in this list carry authored content? */
const groupHasContent = (questions, content) => questions.some((q) => {
  const v = content?.[q.id];
  return Array.isArray(v) ? v.length > 0 : !!(v && String(v).trim());
});

/** "Campaign 2" from "Campaign 2: Name" — the block heading, so the per-field
 *  prefix can be stripped inside the block instead of repeated eight times. */
const groupHeading = (questions, key) => {
  const m = String(questions[0]?.label || '').match(/^(.+?):\s/);
  return m ? m[1] : key.toUpperCase();
};

/** One campaign-style block: heading + its fields with the heading prefix stripped. */
function blockHtml(questions, heading, content, { hidden = false } = {}) {
  const prefix = `${heading}: `;
  const key = questions[0]?.group || '';
  return `
      <div class="dz-cblock" data-group="${esc(key)}" data-heading="${esc(heading)}"${hidden ? ' hidden' : ''}>
        <h4 class="dz-cblock__title">${esc(heading)}</h4>
        ${questions.map((q) => authorFieldHtml(q, content, q.label.startsWith(prefix) ? q.label.slice(prefix.length) : q.label)).join('')}
      </div>`;
}

/**
 * One section's authoring body. Ungrouped am questions render flat; grouped
 * questions render as blocks. Two block regimes:
 *   - repeat sections (v3+): the template holds ONE prototype instance;
 *     blocks materialize for every instance the content already holds, and
 *     "+ Add Campaign N" appends a fresh one with NO ceiling.
 *   - explicit groups (v1/v2, pinned rows): the fixed blocks reveal
 *     progressively up to however many the template authored.
 * Hidden or empty blocks collect nothing (empty values are omitted).
 */
function authorSectionBodyHtml(section, content) {
  const amQuestions = (section.questions || []).filter((q) => q.audience === 'am');
  const flat = amQuestions.filter((q) => !q.group);
  let html = flat.map((q) => authorFieldHtml(q, content)).join('');

  const family = repeatFamily(section);
  if (family) {
    const proto = prototypeQuestions(section).filter((q) => q.audience === 'am');
    if (!proto.length) return html;
    const count = maxInstance(section, content);
    for (let n = 1; n <= count; n++) {
      const qs = instanceQuestions(proto, family, n);
      html += blockHtml(qs, groupHeading(qs, `${family}${n}`), content);
    }
    const nextHeading = groupHeading(instanceQuestions(proto, family, count + 1), `${family}${count + 1}`);
    html += `<button class="dz-btn--ghost dz-addblock" type="button" data-repeat="${esc(family)}" data-section-id="${esc(section.id)}">+ Add ${esc(nextHeading)}</button>`;
    return html;
  }

  const groupKeys = [...new Set(amQuestions.filter((q) => q.group).map((q) => q.group))];
  if (!groupKeys.length) return html;

  let firstHidden = null;
  html += groupKeys.map((key, i) => {
    const qs = amQuestions.filter((q) => q.group === key);
    const heading = groupHeading(qs, key);
    const visible = i === 0 || groupHasContent(qs, content);
    if (!visible && firstHidden === null) firstHidden = { key, heading };
    return blockHtml(qs, heading, content, { hidden: !visible });
  }).join('');

  html += `<button class="dz-btn--ghost dz-addblock" type="button"${firstHidden ? '' : ' hidden'}>${firstHidden ? `+ Add ${esc(firstHidden.heading)}` : ''}</button>`;
  return html;
}

function authorSectionsHtml(template, content) {
  const sections = (template?.sections || []).filter((s) => (s.questions || []).some((q) => q.audience === 'am'));
  const multi = sections.length > 1;
  return `
    ${multi ? `
      <div class="dz-steps-head">
        <span class="dz-step-label" id="dz-step-label"></span>
        <div class="dz-steps-ctl">
          <button type="button" class="dz-arrow" id="dz-step-prev" aria-label="Previous section"><span class="material-symbols-outlined" aria-hidden="true">chevron_left</span></button>
          <div class="dz-dots" role="group" aria-label="Sections">
            ${sections.map((s, i) => `<button type="button" class="dz-dot" data-step="${i}" aria-label="${esc(s.title)}"></button>`).join('')}
          </div>
          <button type="button" class="dz-arrow" id="dz-step-fwd" aria-label="Next section"><span class="material-symbols-outlined" aria-hidden="true">chevron_right</span></button>
        </div>
      </div>` : ''}
    ${sections.map((s, i) => `
      <div class="qm-section dz-step" data-step="${i}"${multi && i > 0 ? ' hidden' : ''}>
        <h3>${esc(s.title)}</h3>
        ${s.intro ? `<p class="dz-intro">${esc(s.intro)}</p>` : ''}
        ${authorSectionBodyHtml(s, content)}
      </div>`).join('')}
    ${multi ? `
      <div class="dz-step-nav">
        <button class="dz-btn--ghost" type="button" id="dz-step-back">Back</button>
        <button class="dz-btn" type="button" id="dz-step-next">Next</button>
      </div>` : ''}`;
}

/**
 * Wire the authoring roller + the add-campaign buttons. Steps stay MOUNTED
 * (hidden, not unmounted) so typed content survives navigation and one
 * collectAnswers reads everything. `onStepChange` autosaves in passing —
 * "stateful" means a step hop never costs work.
 */
function wireAuthorStepper(formEl, template, onStepChange) {
  formEl.querySelectorAll('.dz-addblock').forEach((btn) => {
    btn.addEventListener('click', () => {
      const scope = btn.parentElement;
      // Repeat family (v3+): mint a brand-new instance from the prototype —
      // there is no ceiling to hit.
      if (btn.dataset.repeat) {
        const section = (template?.sections || []).find((s) => s.id === btn.dataset.sectionId);
        const proto = prototypeQuestions(section).filter((q) => q.audience === 'am');
        if (!proto.length) return;
        const family = btn.dataset.repeat;
        const n = scope.querySelectorAll('.dz-cblock').length + 1;
        const qs = instanceQuestions(proto, family, n);
        btn.insertAdjacentHTML('beforebegin', blockHtml(qs, groupHeading(qs, `${family}${n}`), {}));
        const nextQs = instanceQuestions(proto, family, n + 1);
        btn.textContent = `+ Add ${groupHeading(nextQs, `${family}${n + 1}`)}`;
        return;
      }
      // Explicit groups (v1/v2 pinned rows): reveal the next fixed block.
      const hidden = [...scope.querySelectorAll('.dz-cblock[hidden]')];
      const next = hidden[0];
      if (next) next.hidden = false;
      const after = hidden[1];
      if (after) btn.textContent = `+ Add ${after.dataset.heading}`;
      else btn.hidden = true;
    });
  });

  const steps = [...formEl.querySelectorAll('.dz-step')];
  if (steps.length <= 1) return;
  const dots = [...formEl.querySelectorAll('.dz-dot')];
  const label = formEl.querySelector('#dz-step-label');
  const backBtn = formEl.querySelector('#dz-step-back');
  const nextBtn = formEl.querySelector('#dz-step-next');
  const prevArrow = formEl.querySelector('#dz-step-prev');
  const fwdArrow = formEl.querySelector('#dz-step-fwd');
  let step = 0;

  const show = (n) => {
    step = Math.max(0, Math.min(steps.length - 1, n));
    steps.forEach((el, i) => { el.hidden = i !== step; });
    dots.forEach((d, i) => {
      if (i === step) d.setAttribute('aria-current', 'step');
      else d.removeAttribute('aria-current');
    });
    if (label) label.textContent = `Step ${step + 1} of ${steps.length} · ${steps[step].querySelector('h3')?.textContent || ''}`;
    backBtn.style.visibility = step === 0 ? 'hidden' : '';
    nextBtn.style.visibility = step === steps.length - 1 ? 'hidden' : '';
    if (prevArrow) prevArrow.disabled = step === 0;
    if (fwdArrow) fwdArrow.disabled = step === steps.length - 1;
    formEl.scrollIntoView?.({ block: 'start' });
  };

  const go = (n) => { show(n); onStepChange?.(); };
  backBtn.addEventListener('click', () => go(step - 1));
  nextBtn.addEventListener('click', () => go(step + 1));
  prevArrow?.addEventListener('click', () => go(step - 1));
  fwdArrow?.addEventListener('click', () => go(step + 1));
  dots.forEach((d) => d.addEventListener('click', () => go(Number(d.dataset.step) || 0)));
  show(0);
}

// ── review (read-only) rendering: authored plan + the client's answers ───────
function reviewSectionsHtml(template, content, answers) {
  return expandSections(template?.sections || [], content, answers).map((s) => {
    const qs = (s.questions || []).filter((q) => {
      if (q.audience !== 'am') return true; // client review fields always show
      const v = content?.[q.id];
      return Array.isArray(v) ? v.length > 0 : !!(v && String(v).trim());
    });
    if (!qs.length) return '';
    return `
      <div class="qm-section">
        <h3>${esc(s.title)}</h3>
        ${qs.map((q) => `
          <div class="qm-qa">
            <div class="qm-qa__q">${esc(q.label)}</div>
            <div class="qm-qa__a">${q.audience === 'am'
              ? answerDisplay(q, content)
              : (answerDisplay(q, answers) || '<span class="qm-qa__a--empty">Left blank</span>')}</div>
          </div>`).join('')}
      </div>`;
  }).join('');
}

/**
 * One archived round's client feedback as a titled block — the non-empty
 * review fields, labelled from the template. Empty rounds (a blank
 * accidental submit) say so instead of rendering nothing, so history never
 * silently skips a round number.
 */
function roundFeedbackHtml(template, round) {
  if (!round) return '';
  const items = (template?.sections || [])
    .flatMap((s) => (s.questions || []).filter((q) => q.audience !== 'am'))
    .map((q) => ({ q, val: answerDisplay(q, round.answers || {}) }))
    .filter(({ val }) => val);
  return `
    <div class="dz-feedback">
      <p class="dz-feedback__title">Round ${esc(round.round)} feedback${round.submitted_tz ? ` · returned ${esc(fmtDateTime(round.submitted_tz))}` : ''}</p>
      ${items.length ? items.map(({ q, val }) => `
        <div class="qm-qa">
          <div class="qm-qa__q">${esc(q.label)}</div>
          <div class="qm-qa__a">${val}</div>
        </div>`).join('') : '<div class="qm-qa__a qm-qa__a--empty">Returned without feedback.</div>'}
    </div>`;
}

/**
 * The client's feedback from the last completed round, pinned above the
 * authoring form after a revise — the whole point of revising is to edit
 * against exactly these notes.
 */
function lastRoundFeedbackHtml(template, rounds) {
  const last = Array.isArray(rounds) && rounds.length ? rounds[rounds.length - 1] : null;
  return last ? roundFeedbackHtml(template, last) : '';
}

/**
 * Every archived round, newest first — shown under the CURRENT review in the
 * read drawer so a blank latest round (an accidental empty re-submit, which
 * happened live) can never blind the AM to earlier feedback.
 */
function roundsHistoryHtml(template, rounds) {
  const list = Array.isArray(rounds) ? rounds.slice().reverse() : [];
  return list.map((r) => roundFeedbackHtml(template, r)).join('');
}

// ── the main drawer: authoring for DRAFT/SENT, read-only review for the rest ─
async function openDrawer(guid) {
  const { drawer, close } = makeDrawer('Deliverable');
  const body = drawer.querySelector('#dz-dbody');
  const foot = drawer.querySelector('#dz-dfoot');

  let data;
  try {
    const res = await guardedFetch(`${apiBase()}/m5t/v5/m5mDeliverable/${encodeURIComponent(guid)}`);
    const json = await res.json().catch(() => ({}));
    failIfNotOk(res, json);
    if (!json.ok) throw new Error(json.error || 'load failed');
    data = json.data || {};
  } catch (err) {
    body.innerHTML = isDenial(err) ? denialState() : emptyState('error', 'Could not load this deliverable. Please try again.');
    return;
  }

  const clientName = state.rows.find((r) => r.guid === guid)?.owner_name
    || state.clients?.find((c) => c.guid === data.owner_guid)?.caption || 'Client';
  // ' · ' is the house separator (no em dashes in microcopy — brand rule).
  drawer.querySelector('#dz-dtitle').textContent = `${clientName} · ${data.template?.name || 'Deliverable'}`;

  const authoring = data.status === 'DRAFT' || data.status === 'SENT';
  const rev = Number(data.revision) > 1 ? ` · Rev ${data.revision}` : '';
  const sub = data.status === 'SUBMITTED'
    ? `Returned ${data.submitted_tz ? fmtDateTime(data.submitted_tz) : ''}${data.locked_tz ? ' · Finalized' : ''}${rev}`
    : data.status === 'SENT'
      ? `Sent ${data.sent_tz ? fmtDateTime(data.sent_tz) : ''} to ${data.recipient_email || ''} · awaiting approval${rev}`
      : `${STATUS_LABEL[data.status] || data.status}${rev}`;
  drawer.querySelector('#dz-dsub').textContent = sub;

  if (!authoring) {
    body.innerHTML = answersMetaHtml(data)
      + reviewSectionsHtml(data.template, data.content || {}, data.answers || {})
      + roundsHistoryHtml(data.template, data.rounds);
    // Revise: the answer to a "Requested Changes" review. Archives this round
    // and reopens authoring — not offered on a finalized (or cancelled) record.
    if (data.status === 'SUBMITTED' && !data.locked_tz) {
      foot.innerHTML = `<button class="dz-btn" type="button" id="dz-revise">Revise plan</button>
        <span class="dz-note" style="margin:0">Archives this review and reopens authoring. The client approves the revised plan on a fresh link.</span>`;
      foot.querySelector('#dz-revise').addEventListener('click', async (e) => {
        const btn = e.target;
        btn.disabled = true;
        try {
          const res = await guardedFetch(`${apiBase()}/m5t/v5/m5mDeliverable/${encodeURIComponent(guid)}/revise`, { method: 'POST' });
          const json = await res.json().catch(() => ({}));
          if (!res.ok || !json.ok) throw new Error(json.error || 'revise failed');
          toast('Reopened for revision. The last review is pinned above the form.', 'success');
          close();
          await load();
          openDrawer(guid);
        } catch (err) {
          toast(err.message || 'Could not revise.', 'error');
          btn.disabled = false;
        }
      });
    } else {
      foot.innerHTML = '';
    }
    return;
  }

  // ── authoring mode ──
  // The link exists from creation (questionnaire-style): the same URL is the
  // preview while DRAFT and the live review once sent.
  body.innerHTML = `
    ${lastRoundFeedbackHtml(data.template, data.rounds)}
    ${data.url ? `
      <div class="dz-linkrow">
        <input type="text" readonly value="${esc(data.url)}" aria-label="Deliverable link" />
        <button class="dz-btn--ghost" type="button" id="dz-copylink">Copy link</button>
      </div>
      <p class="dz-note">${data.status === 'SENT'
        ? 'The client already has this link. Saving updates the plan they see; if they had it open, they are asked to reload before approving. Re-sending emails the same link again.'
        : 'The live review link. Send to client emails it, and you can also share it directly.'}</p>` : ''}
    <div id="dz-author-form">${authorSectionsHtml(data.template, data.content || {})}</div>`;

  foot.innerHTML = `
    <button class="dz-btn--ghost" type="button" id="dz-preview"><span class="material-symbols-outlined" aria-hidden="true">visibility</span>Preview</button>
    <button class="dz-btn--ghost" type="button" id="dz-save">Save draft</button>
    <span class="dz-sendrow">
      <input type="email" id="dz-email" placeholder="client@example.com" value="${esc(data.recipient_email || '')}" aria-label="Recipient email" />
      <button class="dz-btn" type="button" id="dz-send"><span class="material-symbols-outlined" aria-hidden="true">send</span>${data.status === 'SENT' ? 'Re-send' : 'Send to client'}</button>
    </span>
    <button class="dz-btn--ghost dz-btn--danger" type="button" id="dz-cancel">Cancel deliverable</button>`;

  body.querySelector('#dz-copylink')?.addEventListener('click', (e) => {
    const input = body.querySelector('.dz-linkrow input');
    navigator.clipboard?.writeText(input?.value || '').then(
      () => { e.target.textContent = 'Copied!'; setTimeout(() => { e.target.textContent = 'Copy link'; }, 1500); },
      () => toast('Could not copy the link.', 'error'),
    );
  });

  const saveContent = async () => {
    // Reads EVERY step — hidden steps stay mounted, and unrevealed empty
    // campaign blocks contribute nothing (empty values are omitted).
    const content = collectAnswers(body.querySelector('#dz-author-form'));
    const res = await guardedFetch(`${apiBase()}/m5t/v5/m5mDeliverable/${encodeURIComponent(guid)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json.ok) {
      // 409: submitted or cancelled while this drawer sat open. The refusal is
      // correct — reload so the drawer shows what the record now is.
      if (res.status === 409) {
        toast(json.error || 'This deliverable is no longer editable.', 'warning');
        close();
        await load();
        return false;
      }
      throw new Error(json.error || 'save failed');
    }
    return true;
  };

  // The roller: autosaves on every step hop, so navigating never costs work
  // and a half-authored plan survives an accidental close.
  wireAuthorStepper(body.querySelector('#dz-author-form'), data.template, () => {
    saveContent().catch(() => toast('Autosave failed. Use Save draft.', 'warning'));
  });

  foot.querySelector('#dz-preview').addEventListener('click', async (e) => {
    const btn = e.target.closest('button');
    if (!data.url) { toast('No link available for this deliverable.', 'warning'); return; }
    btn.disabled = true;
    try {
      // Save first so the preview shows exactly what is on screen right now.
      // The preview IS the deliverable's own public link — a DRAFT renders
      // read-only there, and it survives every auth state by the same
      // machinery every emailed link uses (no authed route to get bounced off).
      if (await saveContent()) window.open(data.url, '_blank');
    } catch (err) {
      toast(err.message || 'Could not save before previewing.', 'error');
    }
    btn.disabled = false;
  });

  foot.querySelector('#dz-save').addEventListener('click', async (e) => {
    const btn = e.target;
    btn.disabled = true;
    try {
      if (await saveContent()) toast('Draft saved.', 'success');
    } catch (err) {
      toast(err.message || 'Could not save.', 'error');
    }
    btn.disabled = false;
  });

  foot.querySelector('#dz-send').addEventListener('click', async (e) => {
    const btn = e.target;
    const email = (foot.querySelector('#dz-email')?.value || '').trim();
    if (!email) { toast('Enter the recipient email first.', 'warning'); return; }
    btn.disabled = true;
    try {
      // Save first so the client link opens on what is on screen, then send.
      if (!(await saveContent())) return;
      const res = await guardedFetch(`${apiBase()}/m5t/v5/m5mDeliverable/${encodeURIComponent(guid)}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipient_email: email }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) throw new Error(json.error || 'send failed');
      toast(json.data?.emailQueued
        ? `Sent. ${email} will receive the review link.`
        : 'Link created, but the email could not be queued. Copy the link and send it yourself.',
      json.data?.emailQueued ? 'success' : 'warning');
      close();
      await load();
    } catch (err) {
      toast(err.message || 'Could not send.', 'error');
      btn.disabled = false;
    }
  });

  foot.querySelector('#dz-cancel').addEventListener('click', async (e) => {
    const btn = e.target;
    btn.disabled = true;
    try {
      const res = await guardedFetch(`${apiBase()}/m5t/v5/m5mDeliverable/${encodeURIComponent(guid)}/cancel`, { method: 'POST' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) throw new Error(json.error || 'cancel failed');
      toast('Deliverable cancelled. Its link no longer works.', 'info');
      close();
      await load();
    } catch (err) {
      toast(err.message || 'Could not cancel.', 'error');
      btn.disabled = false;
    }
  });
}

export default { render, init };
